import React from 'react';
import { DocumentFormContext, FormRetryData } from './DocumentFormContext';
import { GearDocumentMetadata } from '../../Model/GearDocumentMetadata';
import { CONTENT_TYPE_FIELD, DOC_ID_FIELD } from '../../config/consts';
import { EditedDocument } from '../../components/grid/toolbar/FilterActions.types';

interface DocumentFormProviderProps {
  children: React.ReactNode;
}

export const DocumentFormProvider: React.FC<DocumentFormProviderProps> = ({ children }) => {
  const [editedDocument, setEditedDocument] = React.useState<'new' | EditedDocument | undefined>(undefined);
  const [isRetry, setIsRetry] = React.useState<boolean>(false);
  const [fileUploadRequired, setFileUploadRequired] = React.useState<boolean>(false);
  const [initialFormData, setInitialFormData] = React.useState<{
    metadata?: GearDocumentMetadata;
    contentType?: string;
  }>();

  const openNewDocumentForm = React.useCallback((initialMetadata?: GearDocumentMetadata, contentType?: string) => {
    setInitialFormData(initialMetadata || contentType ? { metadata: initialMetadata, contentType } : undefined);
    setIsRetry(false);
    setEditedDocument('new');
  }, []);

  const openEditDocumentForm = React.useCallback((document: EditedDocument, initialMetadata?: GearDocumentMetadata) => {
    setInitialFormData(initialMetadata ? { metadata: initialMetadata } : undefined);
    setIsRetry(false);
    setEditedDocument(document);
  }, []);

  const openRetryForm = React.useCallback((retryData: FormRetryData) => {
    setInitialFormData({
      metadata: retryData.metadata,
      contentType: retryData.contentType
    });
    setIsRetry(true);
    setFileUploadRequired(retryData.fileUploadRequired);

    if (retryData.formType === 'new') {
      setEditedDocument('new');
    } else if (retryData.documentId) {
      setEditedDocument({
        [CONTENT_TYPE_FIELD]: retryData.contentType,
        [DOC_ID_FIELD]: retryData.documentId
      });
    }
  }, []);

  const closeDocumentForm = React.useCallback(() => {
    setEditedDocument(undefined);
    setInitialFormData(undefined);
    setIsRetry(false);
  }, []);

  const value = React.useMemo(
    () => ({
      openNewDocumentForm,
      openEditDocumentForm,
      openRetryForm,
      closeDocumentForm,
      isFormOpen: editedDocument !== undefined,
      editedDocument,
      initialFormData,
      isRetry,
      fileUploadRequired
    }),
    [
      openNewDocumentForm,
      openEditDocumentForm,
      openRetryForm,
      closeDocumentForm,
      editedDocument,
      initialFormData,
      isRetry,
      fileUploadRequired
    ]
  );

  return <DocumentFormContext.Provider value={value}>{children}</DocumentFormContext.Provider>;
};
